import { ErrorMessage, Spinner, SuccessPopup } from "@/common/components";
import { useContext, useEffect, useSelector, useState } from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import { useSaveProductDetailsMutation } from "@/service/ProductService";
import { setProductDetails } from "@/store/productSlice";
import { Constants } from "@/utils/Constants";
import { scrollToTop } from "@/utils/Utilities";
import { validate } from "@/utils/Validations";
import { useParams } from "react-router";
import ProductPageOne from "./Common/ProductPageOne";
import ProductPageThree from "./Common/ProductPageThree";
import ProductPageTwo from "./Common/ProductPageTwo";
import { ProductContext } from "./ViewProductDetails";

const AddNewProduct = () => {
  const {
    labels,
    setPageHeader,
    setNavHeader,
    debounceState,
    debounceErrors,
    navigate,
    Routes,
    isRiccAdministrator,
    cloneDeep,
    setNavCancelFunc,
    dispatch,
    productSponsorId,
  } = useContext(PrivateContext);

  const params = useParams();
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [productData, setProductData] = useState({});
  const [closeDateCheckbox, setCloseDateCheckbox] = useState(false);
  const [reopenDateCheckbox, setReopenDateCheckbox] = useState(false);

  const productDataFromStore = useSelector(
    (state) => state?.productsReducer?.productDetails
  );

  const [saveProductApi, { isLoading, error }] =
    useSaveProductDetailsMutation();

  const onChange = async (value, state) => {
    debounceState({ ...productData, [state]: value }, setProductData);
    debounceErrors(
      { ...errors, [state]: await validate(value, [state]) },
      setErrors
    );
  };

  const errorProps = (state) => {
    return {
      value: productData[state],
      error: errors[state],
      touched: touched[state],
      touch: () => setTouched({ ...touched, [state]: true }),
    };
  };

  const backToProductList = () => {
    dispatch(setProductDetails({}));
    navigate(Routes.productList.path);
    return;
  };

  const saveProduct = () => {
    scrollToTop();
    let payload = cloneDeep(productData);

    payload.productSponsorId = productSponsorId;
    payload.productOptionSettings.forEach((e) => {
      const fieldValue = productData[e.type];
      const matchesProductType =
        e.productType === productData.productType || e.productType === "ALL";
      if (fieldValue !== undefined && matchesProductType) {
        e.value = fieldValue;
        e.productSponsorId = productSponsorId;
      }
    });

    if (!closeDateCheckbox) {
      payload.closedDt = "";
    }

    if (!reopenDateCheckbox) {
      payload.reopenDt = "";
    }

    saveProductApi(payload)
      .unwrap()
      .then((res) => {
        if (!res.inError) {
          setShowPopup(true);
        }
      });
  };

  useEffect(() => {
    setPageHeader(
      params?.id == 1
        ? labels?.addProduct?.title
        : labels?.addNewProduct?.productOptions
    );
    setNavHeader(labels?.navbar?.product);
    setNavCancelFunc(() => backToProductList);
  }, [params]);

  useEffect(() => {
    if (productDataFromStore?.name) {
      setProductData(cloneDeep(productDataFromStore));
    } else {
      setProductData(Constants.defaultProductData);
    }
  }, []);

  if (productData?.productOptionSettings !== undefined)
    return (
      <ProductContext.Provider
        value={{
          labels,
          onChange,
          errorProps,
          productData,
          setProductData,
          readOnlyAccess: false,
          isRiccAdministrator,
          readOnlyModeForPaymentTableAgeRange: () => false,
          closeAtCheckboxDisabled: true,
          back: () => navigate(-1),
          errors,
          closeDateCheckbox,
          setCloseDateCheckbox,
          reopenDateCheckbox,
          setReopenDateCheckbox,
          disabledActiveDate: false,
          addProduct: true,
        }}
      >
        {isLoading && <Spinner />}
        <ErrorMessage error={error} wrapperClassName="pt-3" />
        {params?.id == 1 ? (
          <ProductPageOne
            next={() => {
              dispatch(setProductDetails(productData));
              navigate(Routes.addNewProduct.path?.replace(":id", 2), {
                state: productData,
              });
            }}
          />
        ) : params?.id == 2 ? (
          <ProductPageTwo
            next={() => {
              dispatch(setProductDetails(productData));
              navigate(Routes.addNewProduct.path?.replace(":id", 3), {
                state: productData,
              });
            }}
          />
        ) : params?.id == 3 ? (
          <ProductPageThree submit={saveProduct} />
        ) : (
          ""
        )}
        <SuccessPopup
          showModal={showPopup}
          buttonText={labels?.addProduct?.continue}
          click={backToProductList}
          body={labels?.addProduct?.continueMessage}
        />
      </ProductContext.Provider>
    );
};

export default AddNewProduct;
